/**
 * Template Switch Modal Component
 *
 * ConfirmDialog-based dialog for switching note templates on mobile/small screens
 * - Shows current template
 * - Template picker for selection
 * - Single-step regeneration (no separate confirmation)
 * - Loading state support
 * - Uses ConfirmDialog for visual consistency with other dialogs
 */

import React, { useState } from 'react';
import TemplatePicker, { type TemplatePickerItem } from '@components/Pickers/TemplatePicker';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { Typography } from '@mui/material';

export interface TemplateSwitchModalProps {
  /**
   * Whether modal is open
   */
  open: boolean;

  /**
   * Close handler
   */
  onClose: () => void;

  /**
   * Current template name for display
   */
  currentTemplateName?: string;

  /**
   * Current template ID (to mark as selected/disabled in picker)
   */
  currentTemplateId?: string;

  /**
   * Template selection handler
   * Called when user confirms template switch
   */
  onTemplateSelect: (template: TemplatePickerItem) => void;

  /**
   * Whether regeneration is in progress
   */
  isRegenerating?: boolean;
}

/**
 * Dialog for switching note templates
 * Streamlined single-step process for mobile UX
 * Uses ConfirmDialog for consistency with other dialogs
 */
export const TemplateSwitchModal: React.FC<TemplateSwitchModalProps> = ({
  open,
  onClose,
  currentTemplateName,
  currentTemplateId,
  onTemplateSelect,
  isRegenerating = false,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplatePickerItem | undefined>();

  const handleTemplatePickerSelect = (template: TemplatePickerItem | undefined) => {
    setSelectedTemplate(template);
  };

  const handleConfirm = () => {
    if (selectedTemplate) {
      onTemplateSelect(selectedTemplate);
    }
  };

  const handleClose = () => {
    if (!isRegenerating) {
      setSelectedTemplate(undefined);
      onClose();
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      variant="warning"
      title="Switch Note Template"
      message={
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Current template: <strong>{currentTemplateName || 'Not specified'}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Select a template to regenerate this note. This will create a new note with the selected
            template.
          </Typography>
        </>
      }
      onConfirm={handleConfirm}
      confirmText="Regenerate Note"
      loading={isRegenerating}
      disableConfirm={!selectedTemplate}
      maxWidth="sm"
      fullWidth
    >
      <TemplatePicker
        initialTemplateId={currentTemplateId}
        onTemplateSelect={handleTemplatePickerSelect}
        label="Select new template"
      />

      <Typography variant="caption" color="error" sx={{ mt: 3, display: 'block' }}>
        ⚠️ This will create a new note. Your current note will remain unchanged.
      </Typography>
    </ConfirmDialog>
  );
};
